import React, { useEffect, useMemo, useState } from "react";

import {
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Empty,
    InputNumber,
    List,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";

import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    PrinterOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    UserOutlined,
    DollarOutlined,
    WalletOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    BankOutlined,
    CreditCardOutlined,
    RiseOutlined,
    PercentageOutlined,
    EyeOutlined,
    ReloadOutlined,
} from "@ant-design/icons";

import api from "../../api/api";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AvatarStudent from "./studentavv.png";
import ReceiptDialog from "./ReceiptDialog";

import { useAcademicYear } from "../../AcademicYearContext";

const { Title, Text } = Typography;

const FEE_TYPE_CHOICES = [
    {
        value: "registration",
        label: "Registration",
    },
    {
        value: "first_installment",
        label: "1st Installment",
    },
    {
        value: "second_installment",
        label: "2nd Installment",
    },
    {
        value: "third_installment",
        label: "3rd Installment",
    },
    {
        value: "fourth_installment",
        label: "4th Installment",
    },
];

const ORANGE = "#ff6b00";
const BLACK = "#0d0d0d";
const DARK_CARD = "#171717";
const CARD = "#1d1d1d";
const SOFT_BLACK = "#242424";
const WHITE = "#ffffff";
const MUTED = "#a7a7a7";
const BORDER = "#333333";
const GREEN = "#52c41a";
const BLUE = "#1677ff";
const RED = "#ff4d4f";

const getPaymentCurrency = (record) =>
    record?.payment_currency === "USD"
        ? "USD"
        : "LRD";

const getPaymentExchangeRate = (record) =>
    Number(record?.exchange_rate || 0);

const getOriginalPaymentAmount = (record) => {
    if (
        record?.amount_paid !== undefined &&
        record?.amount_paid !== null
    ) {
        return Number(record.amount_paid || 0);
    }

    const amount = Number(record?.amount || 0);
    const currency = getPaymentCurrency(record);
    const exchangeRate = getPaymentExchangeRate(record);

    if (
        currency === "USD" &&
        exchangeRate > 0 &&
        record?.amount_paid_lrd > 0
    ) {
        return amount / exchangeRate;
    }

    return amount;
};

const getNormalizedAmount = (record) => {
    const amountPaidLrd = Number(
        record?.amount_paid_lrd || 0
    );

    if (amountPaidLrd > 0) {
        return amountPaidLrd;
    }

    const amount = Number(
        record?.amount_paid ??
            record?.amount ??
            0
    );

    const currency =
        getPaymentCurrency(record);

    const exchangeRate =
        getPaymentExchangeRate(record);

    if (
        currency === "USD" &&
        exchangeRate > 0
    ) {
        return amount * exchangeRate;
    }

    return amount;
};

const formatMoney = (value) =>
    Number(value || 0).toLocaleString(
        undefined,
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    );

const formatCurrency = (
    value,
    currency = "LRD"
) =>
    `${currency} ${formatMoney(value)}`;

const getFeeTypeLabel = (value) => {
    const found = FEE_TYPE_CHOICES.find(
        (item) => item.value === value
    );

    return found?.label || value || "-";
};

const OtherFee = () => {
    const { selectedYear } =
        useAcademicYear();

    const [receiptDialogOpen, setReceiptDialogOpen] =
        useState(false);

    const [students, setStudents] =
        useState([]);

    const [fees, setFees] =
        useState([]);

    const [groupedRows, setGroupedRows] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [selectedStudentCreate, setSelectedStudentCreate] =
        useState(null);

    const [gradeClassNameCreate, setGradeClassNameCreate] =
        useState("");

    const [amountCreate, setAmountCreate] =
        useState(0);

    const [balanceCreate, setBalanceCreate] =
        useState(0);

    const [feeChargeCreate, setFeeChargeCreate] =
        useState(0);

    const [paymentCurrencyCreate, setPaymentCurrencyCreate] =
        useState("LRD");

    const [exchangeRateCreate, setExchangeRateCreate] =
        useState(0);

    const [allocationsCreate, setAllocationsCreate] =
        useState([]);

    const [selectedStudentDetails, setSelectedStudentDetails] =
        useState(null);

    const [currentFeeIndex, setCurrentFeeIndex] =
        useState(0);

    const [editingFee, setEditingFee] =
        useState(null);

    const [editAllocations, setEditAllocations] =
        useState([]);

    const [editLoading, setEditLoading] =
        useState(false);

    const [deletingFeeId, setDeletingFeeId] =
        useState(null);

    useEffect(() => {
        if (!selectedYear) return;

        fetchStudents();
        fetchFees();
    }, [selectedYear]);

    const fetchStudents = async () => {
        try {
            const response = await api.get(
                "/api/users/students/",
                {
                    params: {
                        academic_year:
                            selectedYear?.id,
                    },
                }
            );

            const studentData =
                response.data?.results ||
                response.data ||
                [];

            setStudents(
                Array.isArray(studentData)
                    ? studentData
                    : []
            );
        } catch (error) {
            console.error(
                "FETCH STUDENTS ERROR:",
                error
            );

            toast.error(
                "Failed to load students."
            );
        }
    };

    const fetchFees = async () => {
        setLoading(true);

        try {
            const response = await api.get(
                "/fees/",
                {
                    params: {
                        academic_year:
                            selectedYear?.id,
                    },
                }
            );

            const list = Array.isArray(
                response.data
            )
                ? response.data
                : response.data?.results ||
                  [];

            setFees(list);
            regroup(list);
        } catch (error) {
            console.error(
                "FETCH FEES ERROR:",
                error
            );

            toast.error(
                "Failed to load fee records."
            );
        } finally {
            setLoading(false);
        }
    };

    const regroup = (feeRecords) => {
        const map = new Map();

        feeRecords.forEach((fee) => {
            const studentId =
                fee.student;

            if (!studentId) return;

            if (!map.has(studentId)) {
                map.set(
                    studentId,
                    []
                );
            }

            map.get(studentId).push(
                fee
            );
        });

        const rows = [];

        for (const [
            studentId,
            records,
        ] of map.entries()) {
            records.sort(
                (a, b) =>
                    new Date(
                        b.created_at
                    ) -
                    new Date(
                        a.created_at
                    )
            );

            const latest =
                records[0];

            rows.push({
                student_id:
                    studentId,

                student_name:
                    latest.student_name ||
                    latest.entrance_student_name ||
                    "-",

                grade_class_name:
                    latest.grade_class_name ||
                    "-",

                student_type:
                    latest.student_type,

                latest_created_at:
                    latest.created_at,

                latest_created_by:
                    latest.created_by_name,

                fee_records:
                    records,
            });
        }

        rows.sort((a, b) =>
            (
                a.student_name ||
                ""
            ).localeCompare(
                b.student_name || ""
            )
        );

        setGroupedRows(rows);
    };

    const addCreateAllocation = () => {
        setAllocationsCreate([
            ...allocationsCreate,
            {
                fee_type: null,
                amount: 0,
            },
        ]);
    };

    const changeCreateAllocation = (
        index,
        field,
        value
    ) => {
        const updated =
            allocationsCreate.map(
                (allocation) => ({
                    ...allocation,
                })
            );

        updated[index][field] =
            field === "amount"
                ? Number(value || 0)
                : value;

        const totalAllocated =
            updated.reduce(
                (sum, allocation) =>
                    sum +
                    Number(
                        allocation.amount ||
                            0
                    ),
                0
            );

        if (
            totalAllocated >
            Number(amountCreate)
        ) {
            toast.error(
                "Allocations exceed available LRD amount!"
            );

            return;
        }

        setAllocationsCreate(
            updated
        );

        setBalanceCreate(
            Number(amountCreate) -
                totalAllocated
        );
    };

    const removeCreateAllocation = (
        index
    ) => {
        const updated =
            allocationsCreate.filter(
                (_, i) =>
                    i !== index
            );

        const totalAllocated =
            updated.reduce(
                (sum, allocation) =>
                    sum +
                    Number(
                        allocation.amount ||
                            0
                    ),
                0
            );

        setAllocationsCreate(
            updated
        );

        setBalanceCreate(
            Number(amountCreate) -
                totalAllocated
        );
    };

    const handleCreate = async () => {
        if (!selectedStudentCreate) {
            return toast.error(
                "Select a student."
            );
        }

        const totalAllocated =
            allocationsCreate.reduce(
                (sum, allocation) =>
                    sum +
                    Number(
                        allocation.amount ||
                            0
                    ),
                0
            );

        if (
            totalAllocated >
            Number(amountCreate)
        ) {
            return toast.error(
                "Allocations exceed available LRD amount!"
            );
        }

        if (
            allocationsCreate.some(
                (allocation) =>
                    !allocation.fee_type ||
                    Number(
                        allocation.amount
                    ) <= 0
            )
        ) {
            return toast.error(
                "Each allocation must have a fee type and amount."
            );
        }

        if (!selectedYear?.id) {
            return toast.error(
                "No academic year is currently selected."
            );
        }

        setLoading(true);

        try {
            const payload = {
                student:
                    selectedStudentCreate.id,

                grade_class:
                    Number(
                        selectedStudentCreate.grade_class_id ||
                            0
                    ) || null,

                academic_year:
                    selectedYear.id,

                amount:
                    totalAllocated,

                fee_charge:
                    Number(
                        feeChargeCreate || 0
                    ),

                allocations:
                    allocationsCreate,
            };

            await api.post(
                "/fees/",
                payload
            );

            toast.success(
                "Fee record created successfully."
            );

            setSelectedStudentCreate(
                null
            );

            setGradeClassNameCreate(
                ""
            );

            setAmountCreate(0);

            setBalanceCreate(0);

            setFeeChargeCreate(0);

            setPaymentCurrencyCreate(
                "LRD"
            );

            setExchangeRateCreate(
                0
            );

            setAllocationsCreate(
                []
            );

            await fetchFees();
        } catch (error) {
            console.error(
                "CREATE FEE ERROR:",
                error.response?.data ||
                    error
            );

            const errorData =
                error.response?.data;

            if (
                errorData &&
                typeof errorData ===
                    "object"
            ) {
                Object.entries(
                    errorData
                ).forEach(
                    ([
                        field,
                        messages,
                    ]) => {
                        const message =
                            Array.isArray(
                                messages
                            )
                                ? messages.join(
                                      ", "
                                  )
                                : messages;

                        toast.error(
                            `${field}: ${message}`
                        );
                    }
                );
            } else {
                toast.error(
                    "Failed to create fee record."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const onCreateStudentSelect =
        async (student) => {
            setSelectedStudentCreate(
                student
            );

            if (!student) {
                setGradeClassNameCreate(
                    ""
                );

                setAmountCreate(0);

                setBalanceCreate(0);

                setFeeChargeCreate(0);

                setPaymentCurrencyCreate(
                    "LRD"
                );

                setExchangeRateCreate(
                    0
                );

                setAllocationsCreate(
                    []
                );

                return;
            }

            try {
                const response =
                    await api.get(
                        "/fees/autofill/",
                        {
                            params: {
                                student_id:
                                    student.id,

                                academic_year:
                                    selectedYear?.id,
                            },
                        }
                    );

                const data =
                    response.data || {};

                const currency =
                    getPaymentCurrency(
                        data
                    );

                const exchangeRate =
                    getPaymentExchangeRate(
                        data
                    );

                const normalizedAmount =
                    getNormalizedAmount(
                        data
                    );

                setFeeChargeCreate(
                    Number(
                        data.fee_charge ||
                            0
                    )
                );

                setGradeClassNameCreate(
                    data.grade_class_name ||
                        student.grade_class_name ||
                        ""
                );

                setPaymentCurrencyCreate(
                    currency
                );

                setExchangeRateCreate(
                    exchangeRate
                );

                setAmountCreate(
                    normalizedAmount
                );

                setBalanceCreate(
                    normalizedAmount
                );

                setAllocationsCreate(
                    Array.isArray(
                        data.allocations
                    )
                        ? data.allocations
                        : []
                );
            } catch (error) {
                console.error(
                    "AUTOFILL ERROR:",
                    error
                );

                toast.error(
                    "Failed to fetch student fee data."
                );
            }
        };

    const formatDate = (iso) => {
        if (!iso) return "-";

        const date =
            new Date(iso);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "-";
        }

        return date.toLocaleString(
            undefined,
            {
                dateStyle:
                    "medium",

                timeStyle:
                    "short",
            }
        );
    };

    const openEdit = (
        feeRecord
    ) => {
        setEditingFee(
            feeRecord
        );

        setEditAllocations(
            (
                feeRecord.allocations ||
                []
            ).map(
                (allocation) => ({
                    ...allocation,
                })
            )
        );
    };

    const closeEdit = () => {
        setEditingFee(null);
        setEditAllocations([]);
    };

    const addEditAllocation = () => {
        setEditAllocations([
            ...editAllocations,
            {
                fee_type: null,
                amount: 0,
            },
        ]);
    };

    const changeEditAllocation = (
        index,
        field,
        value
    ) => {
        const updated =
            editAllocations.map(
                (allocation) => ({
                    ...allocation,
                })
            );

        updated[index][field] =
            field === "amount"
                ? Number(value || 0)
                : value;

        const totalAllocated =
            updated.reduce(
                (sum, allocation) =>
                    sum +
                    Number(
                        allocation.amount ||
                            0
                    ),
                0
            );

        const availableAmount =
            getNormalizedAmount(
                editingFee
            );

        if (
            totalAllocated >
            availableAmount
        ) {
            toast.error(
                "Allocations exceed available LRD amount!"
            );

            return;
        }

        setEditAllocations(
            updated
        );
    };

    const removeEditAllocation = (
        index
    ) => {
        setEditAllocations(
            editAllocations.filter(
                (_, i) =>
                    i !== index
            )
        );
    };

    const updateFeeLocally = (
        updatedFee
    ) => {
        setFees(
            (previousFees) =>
                previousFees.map(
                    (fee) =>
                        fee.id ===
                        updatedFee.id
                            ? updatedFee
                            : fee
                )
        );

        setGroupedRows(
            (previousRows) =>
                previousRows.map(
                    (row) => ({
                        ...row,

                        fee_records:
                            row.fee_records.map(
                                (fee) =>
                                    fee.id ===
                                    updatedFee.id
                                        ? updatedFee
                                        : fee
                            ),
                    })
                )
        );

        setSelectedStudentDetails(
            (previousDetails) => {
                if (
                    !previousDetails
                ) {
                    return previousDetails;
                }

                return {
                    ...previousDetails,

                    fee_records:
                        previousDetails.fee_records.map(
                            (fee) =>
                                fee.id ===
                                updatedFee.id
                                    ? updatedFee
                                    : fee
                        ),
                };
            }
        );
    };

    const saveEdit = async () => {
        if (!editingFee) return;

        const total =
            editAllocations.reduce(
                (sum, allocation) =>
                    sum +
                    Number(
                        allocation.amount ||
                            0
                    ),
                0
            );

        const availableAmount =
            getNormalizedAmount(
                editingFee
            );

        if (
            total >
            availableAmount
        ) {
            return toast.error(
                "Allocations exceed available LRD amount!"
            );
        }

        if (
            editAllocations.some(
                (allocation) =>
                    !allocation.fee_type ||
                    Number(
                        allocation.amount
                    ) <= 0
            )
        ) {
            return toast.error(
                "Each allocation must have a fee type and amount."
            );
        }

        if (!selectedYear?.id) {
            return toast.error(
                "No academic year is currently selected."
            );
        }

        setEditLoading(true);

        try {
            const payload = {
                academic_year:
                    selectedYear.id,

                allocations:
                    editAllocations,

                amount:
                    total,

                fee_charge:
                    Number(
                        editingFee.fee_charge ||
                            0
                    ),

                remarks:
                    editingFee.remarks ||
                    "",
            };

            const response =
                await api.patch(
                    `/fees/${editingFee.id}/`,
                    payload
                );

            updateFeeLocally(
                response.data
            );

            toast.success(
                "Fee updated successfully."
            );

            closeEdit();
        } catch (error) {
            console.error(
                "SAVE FEE ERROR:",
                error.response?.data ||
                    error
            );

            const errorData =
                error.response?.data;

            if (
                errorData &&
                typeof errorData ===
                    "object"
            ) {
                Object.entries(
                    errorData
                ).forEach(
                    ([
                        field,
                        messages,
                    ]) => {
                        const message =
                            Array.isArray(
                                messages
                            )
                                ? messages.join(
                                      ", "
                                  )
                                : messages;

                        toast.error(
                            `${field}: ${message}`
                        );
                    }
                );
            } else {
                toast.error(
                    "Failed to save changes."
                );
            }
        } finally {
            setEditLoading(false);
        }
    };

    const confirmDelete = (
        id
    ) => {
        setDeletingFeeId(id);
    };

    const doDelete = async () => {
        if (!deletingFeeId) return;

        try {
            await api.delete(
                `/fees/${deletingFeeId}/`
            );

            toast.success(
                "Fee record deleted successfully."
            );

            setDeletingFeeId(
                null
            );

            setSelectedStudentDetails(
                null
            );

            await fetchFees();
        } catch (error) {
            console.error(
                "DELETE ERROR:",
                error
            );

            toast.error(
                "Failed to delete fee record."
            );
        }
    };

    const openDetails = (
        row
    ) => {
        setSelectedStudentDetails(
            row
        );

        setCurrentFeeIndex(0);
    };

    const currentFee =
        selectedStudentDetails
            ?.fee_records?.[
            currentFeeIndex
        ];

    const currentCurrency =
        getPaymentCurrency(
            currentFee
        );

    const currentExchangeRate =
        getPaymentExchangeRate(
            currentFee
        );

    const currentNormalizedAmount =
        getNormalizedAmount(
            currentFee
        );

    const currentOriginalAmount =
        getOriginalPaymentAmount(
            currentFee
        );

    const createAllocatedTotal =
        useMemo(
            () =>
                allocationsCreate.reduce(
                    (sum, allocation) =>
                        sum +
                        Number(
                            allocation.amount ||
                                0
                        ),
                    0
                ),
            [allocationsCreate]
        );

    const createAllocationPercent =
        amountCreate > 0
            ? Math.min(
                  (
                      createAllocatedTotal /
                      amountCreate
                  ) *
                      100,
                  100
              )
            : 0;

    const totalNormalizedPayments =
        useMemo(
            () =>
                fees.reduce(
                    (sum, fee) =>
                        sum +
                        getNormalizedAmount(
                            fee
                        ),
                    0
                ),
            [fees]
        );

    const totalAllocatedFees =
        useMemo(
            () =>
                fees.reduce(
                    (sum, fee) =>
                        sum +
                        Number(
                            fee.amount || 0
                        ),
                    0
                ),
            [fees]
        );

    const totalOutstanding =
        useMemo(
            () =>
                fees.reduce(
                    (sum, fee) =>
                        sum +
                        Number(
                            fee.balance || 0
                        ),
                    0
                ),
            [fees]
        );

    const uniqueStudents =
        groupedRows.length;

    const pageStyle = {
        minHeight: "100vh",
        padding:
            "clamp(14px, 3vw, 32px)",
        background:
            "radial-gradient(circle at 8% 0%, rgba(255,107,0,.18), transparent 24%), radial-gradient(circle at 92% 8%, rgba(22,119,255,.12), transparent 20%), linear-gradient(135deg, #fdfdfd 0%, #ffffff 52%, #d28b60 100%)",
    };

    const mainCardStyle = {
        background:
            "rgba(20, 20, 20, 0.97)",
        border:
            "1px solid rgba(255,107,0,.22)",
        borderRadius: 20,
        boxShadow:
            "0 18px 50px rgba(0,0,0,.32)",
    };

    const glassCardStyle = {
        background:
            "linear-gradient(145deg, rgba(34,34,34,.98), rgba(14,14,14,.98))",
        border:
            "1px solid rgba(255,255,255,.08)",
        borderRadius: 18,
        boxShadow:
            "0 12px 32px rgba(0,0,0,.25)",
    };

    return (
        <div style={pageStyle}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />

            <div className="fee-dashboard-header">
                <div className="dashboard-heading">
                    <div className="heading-icon">
                        <WalletOutlined />
                    </div>

                    <div>
                        <Title
                            level={2}
                            style={{
                                color:
                                    "#f59e0b",
                                margin: 0,
                                fontWeight: 900,
                                letterSpacing:
                                    "-.8px",
                            }}
                        >
                            Fee Allocation Dashboard
                        </Title>

                        <Text
                            style={{
                                color:
                                    MUTED,
                                fontSize: 14,
                            }}
                        >
                            Manage student payments,
                            allocations and
                            financial records
                        </Text>
                    </div>
                </div>

                <div className="academic-year-pill">
                    <span className="year-label">
                        ACTIVE ACADEMIC YEAR
                    </span>

                    <span className="year-value">
                        {selectedYear?.name ||
                            "Academic Year"}
                    </span>
                </div>
            </div>

            <Row
                gutter={[
                    16,
                    16,
                ]}
                style={{
                    marginBottom: 24,
                }}
            >
                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >
                    <Card
                        className="metric-card metric-orange"
                        style={glassCardStyle}
                    >
                        <div className="metric-top">
                            <div className="metric-icon">
                                <UserOutlined />
                            </div>

                            <Tag color="orange">
                                STUDENTS
                            </Tag>
                        </div>

                        <Statistic
                            title={
                                <span className="metric-title">
                                    Students With Records
                                </span>
                            }
                            value={
                                uniqueStudents
                            }
                            valueStyle={{
                                color:
                                    WHITE,
                                fontWeight:
                                    900,
                                fontSize: 30,
                            }}
                        />
                    </Card>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >
                    <Card
                        className="metric-card metric-blue"
                        style={glassCardStyle}
                    >
                        <div className="metric-top">
                            <div className="metric-icon blue">
                                <FileTextOutlined />
                            </div>

                            <Tag color="blue">
                                RECORDS
                            </Tag>
                        </div>

                        <Statistic
                            title={
                                <span className="metric-title">
                                    Fee Records
                                </span>
                            }
                            value={
                                fees.length
                            }
                            valueStyle={{
                                color:
                                    WHITE,
                                fontWeight:
                                    900,
                                fontSize: 30,
                            }}
                        />
                    </Card>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >
                    <Card
                        className="metric-card metric-green"
                        style={glassCardStyle}
                    >
                        <div className="metric-top">
                            <div className="metric-icon green">
                                <CheckCircleOutlined />
                            </div>

                            <Tag color="green">
                                ALLOCATED
                            </Tag>
                        </div>

                        <Statistic
                            title={
                                <span className="metric-title">
                                    Allocated Value
                                </span>
                            }
                            value={
                                formatMoney(
                                    totalAllocatedFees
                                )
                            }
                            prefix="LRD"
                            valueStyle={{
                                color:
                                    WHITE,
                                fontWeight:
                                    900,
                                fontSize: 24,
                            }}
                        />
                    </Card>
                </Col>

                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >
                    <Card
                        className="metric-card metric-red"
                        style={glassCardStyle}
                    >
                        <div className="metric-top">
                            <div className="metric-icon red">
                                <RiseOutlined />
                            </div>

                            <Tag color="red">
                                BALANCE
                            </Tag>
                        </div>

                        <Statistic
                            title={
                                <span className="metric-title">
                                    Outstanding Balance
                                </span>
                            }
                            value={
                                formatMoney(
                                    totalOutstanding
                                )
                            }
                            prefix="LRD"
                            valueStyle={{
                                color:
                                    WHITE,
                                fontWeight:
                                    900,
                                fontSize: 24,
                            }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row
                gutter={[
                    16,
                    16,
                ]}
                style={{
                    marginBottom: 24,
                }}
            >
                <Col
                    xs={24}
                    lg={16}
                >
                    <Card
                        style={{
                            ...mainCardStyle,
                            height: "100%",
                        }}
                    >
                        <div className="overview-banner">
                            <div>
                                <Text className="section-eyebrow">
                                    FINANCIAL OVERVIEW
                                </Text>

                                <Title
                                    level={3}
                                    style={{
                                        color:
                                            WHITE,
                                        margin:
                                            "5px 0",
                                        fontWeight:
                                            900,
                                    }}
                                >
                                    Payment Activity
                                </Title>

                                <Text
                                    style={{
                                        color:
                                            MUTED,
                                    }}
                                >
                                    Normalized financial
                                    values are tracked
                                    in Liberian Dollars.
                                </Text>
                            </div>

                            <div className="overview-wallet">
                                <BankOutlined />
                            </div>
                        </div>

                        <Row
                            gutter={[
                                16,
                                16,
                            ]}
                            style={{
                                marginTop:
                                    20,
                            }}
                        >
                            <Col
                                xs={24}
                                sm={12}
                            >
                                <div className="overview-stat">
                                    <div className="overview-stat-icon">
                                        <DollarOutlined />
                                    </div>

                                    <div>
                                        <Text className="overview-label">
                                            Normalized Payments
                                        </Text>

                                        <div className="overview-value">
                                            LRD{" "}
                                            {formatMoney(
                                                totalNormalizedPayments
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                            >
                                <div className="overview-stat">
                                    <div className="overview-stat-icon purple">
                                        <PercentageOutlined />
                                    </div>

                                    <div>
                                        <Text className="overview-label">
                                            Academic Year
                                        </Text>

                                        <div className="overview-value year-overview">
                                            {selectedYear?.name ||
                                                "-"}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                <Col
                    xs={24}
                    lg={8}
                >
                    <Card
                        style={{
                            ...mainCardStyle,
                            height: "100%",
                        }}
                    >
                        <div className="status-panel">
                            <div className="status-circle">
                                <CheckCircleOutlined />
                            </div>

                            <div>
                                <Text className="section-eyebrow">
                                    PAYMENT SYSTEM
                                </Text>

                                <Title
                                    level={3}
                                    style={{
                                        color:
                                            WHITE,
                                        margin:
                                            "5px 0",
                                        fontWeight:
                                            900,
                                    }}
                                >
                                    ACTIVE
                                </Title>

                                <Text
                                    style={{
                                        color:
                                            MUTED,
                                    }}
                                >
                                    USD and LRD
                                    payments supported
                                </Text>
                            </div>
                        </div>

                        <Divider
                            style={{
                                borderColor:
                                    BORDER,
                            }}
                        />

                        <Space
                            wrap
                            size="middle"
                        >
                            <Tag
                                color="green"
                                icon={
                                    <WalletOutlined />
                                }
                            >
                                LRD
                            </Tag>

                            <Tag
                                color="blue"
                                icon={
                                    <CreditCardOutlined />
                                }
                            >
                                USD
                            </Tag>

                            <Tag
                                color="orange"
                                icon={
                                    <PercentageOutlined />
                                }
                            >
                                Exchange Rate
                            </Tag>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Card
                style={{
                    ...mainCardStyle,
                    marginBottom: 24,
                    overflow: "hidden",
                }}
                bodyStyle={{
                    padding: 0,
                }}
            >
                <div className="create-section-header">
                    <div className="create-title-wrap">
                        <div className="create-title-icon">
                            <PlusOutlined />
                        </div>

                        <div>
                            <Text className="section-eyebrow">
                                PAYMENT PROCESSING
                            </Text>

                            <Title
                                level={3}
                                style={{
                                    color:
                                        WHITE,
                                    margin: 0,
                                    fontWeight:
                                        900,
                                }}
                            >
                                Create Fee Allocation
                            </Title>

                            <Text
                                style={{
                                    color:
                                        MUTED,
                                }}
                            >
                                Select a student to
                                retrieve their latest
                                payment information.
                            </Text>
                        </div>
                    </div>

                    <Badge
                        status="processing"
                        text={
                            <span
                                style={{
                                    color:
                                        "#52c41a",
                                    fontWeight:
                                        800,
                                }}
                            >
                                READY
                            </span>
                        }
                    />
                </div>

                <div className="create-section-body">
                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                        align="middle"
                    >
                        <Col
                            xs={24}
                            md={16}
                            lg={10}
                        >
                            <div className="field-label">
                                <UserOutlined />
                                Student
                            </div>

                            <Select
                                showSearch
                                allowClear
                                size="large"
                                placeholder="Search and select student"
                                style={{
                                    width:
                                        "100%",
                                }}
                                value={
                                    selectedStudentCreate?.id
                                }
                                optionFilterProp="label"
                                options={students.map(
                                    (
                                        student
                                    ) => ({
                                        value:
                                            student.id,

                                        label:
                                            student.full_name,

                                        student,
                                    })
                                )}
                                onChange={(
                                    value
                                ) => {
                                    const student =
                                        students.find(
                                            (
                                                item
                                            ) =>
                                                item.id ===
                                                value
                                        );

                                    onCreateStudentSelect(
                                        student
                                    );
                                }}
                                filterOption={(
                                    input,
                                    option
                                ) =>
                                    option?.label
                                        ?.toLowerCase()
                                        .includes(
                                            input.toLowerCase()
                                        )
                                }
                            />
                        </Col>

                        <Col
                            xs={24}
                            md={8}
                            lg={6}
                        >
                            <div className="quick-info-box">
                                <span>
                                    Academic Year
                                </span>

                                <strong>
                                    {selectedYear?.name ||
                                        "-"}
                                </strong>
                            </div>
                        </Col>

                        <Col
                            xs={24}
                            lg={8}
                        >
                            <div className="quick-info-box currency-box">
                                <span>
                                    Supported Currency
                                </span>

                                <Space>
                                    <Tag color="green">
                                        LRD
                                    </Tag>

                                    <Tag color="blue">
                                        USD
                                    </Tag>
                                </Space>
                            </div>
                        </Col>
                    </Row>

                    {selectedStudentCreate && (
                        <>
                            <Divider
                                style={{
                                    borderColor:
                                        BORDER,
                                    margin:
                                        "28px 0",
                                }}
                            />

                            <Row
                                gutter={[
                                    20,
                                    20,
                                ]}
                            >
                                <Col
                                    xs={24}
                                    lg={7}
                                >
                                    <Card
                                        className="student-profile-create"
                                        style={{
                                            background:
                                                "linear-gradient(145deg, #252525, #101010)",
                                            border:
                                                "1px solid rgba(255,107,0,.35)",
                                            borderRadius:
                                                18,
                                        }}
                                    >
                                        <div className="profile-top">
                                            <div className="avatar-ring">
                                                <Avatar
                                                    src={
                                                        AvatarStudent
                                                    }
                                                    size={
                                                        108
                                                    }
                                                />
                                            </div>

                                            <Tag
                                                color="orange"
                                                style={{
                                                    marginTop:
                                                        10,
                                                }}
                                            >
                                                {
                                                    selectedStudentCreate.student_type ||
                                                    "Regular"
                                                }
                                            </Tag>

                                            <Title
                                                level={
                                                    4
                                                }
                                                style={{
                                                    color:
                                                        WHITE,
                                                    margin:
                                                        "10px 0 2px",
                                                    fontWeight:
                                                        900,
                                                }}
                                            >
                                                {
                                                    selectedStudentCreate.full_name
                                                }
                                            </Title>

                                            <Text
                                                style={{
                                                    color:
                                                        MUTED,
                                                }}
                                            >
                                                {
                                                    gradeClassNameCreate
                                                }
                                            </Text>
                                        </div>

                                        <Divider
                                            style={{
                                                borderColor:
                                                    BORDER,
                                            }}
                                        />

                                        <div className="currency-highlight">
                                            <div className="currency-icon">
                                                {paymentCurrencyCreate ===
                                                "USD" ? (
                                                    <CreditCardOutlined />
                                                ) : (
                                                    <DollarOutlined />
                                                )}
                                            </div>

                                            <div>
                                                <span>
                                                    PAYMENT CURRENCY
                                                </span>

                                                <strong>
                                                    {
                                                        paymentCurrencyCreate
                                                    }
                                                </strong>
                                            </div>
                                        </div>

                                        {paymentCurrencyCreate ===
                                            "USD" &&
                                            exchangeRateCreate >
                                                0 && (
                                                <div className="exchange-rate-box">
                                                    <div>
                                                        <PercentageOutlined />
                                                    </div>

                                                    <div>
                                                        <span>
                                                            EXCHANGE RATE
                                                        </span>

                                                        <strong>
                                                            1 USD =
                                                            {" "}
                                                            {formatMoney(
                                                                exchangeRateCreate
                                                            )}{" "}
                                                            LRD
                                                        </strong>
                                                    </div>
                                                </div>
                                            )}

                                        <div className="financial-mini-grid">
                                            <div>
                                                <span>
                                                    Payment
                                                </span>

                                                <strong>
                                                    {formatCurrency(
                                                        paymentCurrencyCreate ===
                                                            "USD" &&
                                                            exchangeRateCreate >
                                                                0
                                                            ? Number(
                                                                  amountCreate ||
                                                                      0
                                                              ) /
                                                                  exchangeRateCreate
                                                            : amountCreate,
                                                        paymentCurrencyCreate
                                                    )}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>
                                                    Available
                                                </span>

                                                <strong className="orange-value">
                                                    LRD{" "}
                                                    {formatMoney(
                                                        balanceCreate
                                                    )}
                                                </strong>
                                            </div>
                                        </div>

                                        <div className="fee-charge-box">
                                            <span>
                                                FEE CHARGE
                                            </span>

                                            <strong>
                                                LRD{" "}
                                                {formatMoney(
                                                    feeChargeCreate
                                                )}
                                            </strong>
                                        </div>

                                        <Space
                                            direction="vertical"
                                            style={{
                                                width:
                                                    "100%",
                                                marginTop:
                                                    20,
                                            }}
                                            size="middle"
                                        >
                                            <Button
                                                block
                                                size="large"
                                                icon={
                                                    <PlusOutlined />
                                                }
                                                onClick={
                                                    addCreateAllocation
                                                }
                                                style={{
                                                    borderColor:
                                                        ORANGE,
                                                    color:
                                                        ORANGE,
                                                    background:
                                                        "transparent",
                                                    fontWeight:
                                                        800,
                                                }}
                                            >
                                                Add Allocation
                                            </Button>

                                            <Button
                                                block
                                                type="primary"
                                                size="large"
                                                icon={
                                                    <CheckCircleOutlined />
                                                }
                                                onClick={
                                                    handleCreate
                                                }
                                                style={{
                                                    background:
                                                        ORANGE,
                                                    borderColor:
                                                        ORANGE,
                                                    fontWeight:
                                                        900,
                                                    height: 46,
                                                }}
                                            >
                                                Create Fee Record
                                            </Button>
                                        </Space>
                                    </Card>
                                </Col>

                                <Col
                                    xs={24}
                                    lg={17}
                                >
                                    <Card
                                        className="allocation-builder"
                                        style={{
                                            background:
                                                DARK_CARD,
                                            border:
                                                "1px solid #333",
                                            borderRadius:
                                                18,
                                            height:
                                                "100%",
                                        }}
                                        title={
                                            <div className="allocation-header">
                                                <div>
                                                    <Text className="section-eyebrow">
                                                        BREAKDOWN
                                                    </Text>

                                                    <div className="allocation-title">
                                                        Fee Allocations
                                                    </div>
                                                </div>

                                                <Tag
                                                    color="blue"
                                                    icon={
                                                        <WalletOutlined />
                                                    }
                                                >
                                                    LRD ACCOUNTING
                                                </Tag>
                                            </div>
                                        }
                                    >
                                        <div className="allocation-progress">
                                            <div className="allocation-progress-header">
                                                <span>
                                                    Allocation
                                                    Progress
                                                </span>

                                                <strong>
                                                    {formatMoney(
                                                        createAllocatedTotal
                                                    )}{" "}
                                                    /{" "}
                                                    {formatMoney(
                                                        amountCreate
                                                    )}{" "}
                                                    LRD
                                                </strong>
                                            </div>

                                            <Progress
                                                percent={Math.round(
                                                    createAllocationPercent
                                                )}
                                                showInfo={false}
                                                strokeColor={
                                                    ORANGE
                                                }
                                                trailColor="#333"
                                            />

                                            <div className="allocation-progress-footer">
                                                <span>
                                                    Remaining
                                                </span>

                                                <strong>
                                                    LRD{" "}
                                                    {formatMoney(
                                                        balanceCreate
                                                    )}
                                                </strong>
                                            </div>
                                        </div>

                                        <Divider
                                            style={{
                                                borderColor:
                                                    BORDER,
                                            }}
                                        />

                                        <Space
                                            direction="vertical"
                                            style={{
                                                width:
                                                    "100%",
                                            }}
                                            size="middle"
                                        >
                                            {allocationsCreate.length ===
                                            0 ? (
                                                <div className="empty-allocation">
                                                    <div className="empty-icon">
                                                        <FileTextOutlined />
                                                    </div>

                                                    <Title
                                                        level={
                                                            5
                                                        }
                                                        style={{
                                                            color:
                                                                WHITE,
                                                            margin:
                                                                "10px 0 4px",
                                                        }}
                                                    >
                                                        No Allocations Yet
                                                    </Title>

                                                    <Text
                                                        style={{
                                                            color:
                                                                MUTED,
                                                        }}
                                                    >
                                                        Add an allocation
                                                        to distribute
                                                        the student's
                                                        payment.
                                                    </Text>

                                                    <Button
                                                        type="primary"
                                                        icon={
                                                            <PlusOutlined />
                                                        }
                                                        onClick={
                                                            addCreateAllocation
                                                        }
                                                        style={{
                                                            marginTop:
                                                                16,
                                                            background:
                                                                ORANGE,
                                                            borderColor:
                                                                ORANGE,
                                                        }}
                                                    >
                                                        Add First Allocation
                                                    </Button>
                                                </div>
                                            ) : (
                                                allocationsCreate.map(
                                                    (
                                                        allocation,
                                                        index
                                                    ) => (
                                                        <Card
                                                            key={
                                                                index
                                                            }
                                                            size="small"
                                                            className="allocation-row-card"
                                                            style={{
                                                                background:
                                                                    SOFT_BLACK,
                                                                border:
                                                                    "1px solid #383838",
                                                                borderRadius:
                                                                    14,
                                                            }}
                                                        >
                                                            <div className="allocation-number">
                                                                {String(
                                                                    index +
                                                                        1
                                                                ).padStart(
                                                                    2,
                                                                    "0"
                                                                )}
                                                            </div>

                                                            <Row
                                                                gutter={[
                                                                    12,
                                                                    12,
                                                                ]}
                                                                align="middle"
                                                            >
                                                                <Col
                                                                    xs={
                                                                        24
                                                                    }
                                                                    md={
                                                                        10
                                                                    }
                                                                >
                                                                    <div className="field-label">
                                                                        Fee Type
                                                                    </div>

                                                                    <Select
                                                                        style={{
                                                                            width:
                                                                                "100%",
                                                                        }}
                                                                        size="large"
                                                                        placeholder="Select Fee Type"
                                                                        value={
                                                                            allocation.fee_type
                                                                        }
                                                                        options={FEE_TYPE_CHOICES.map(
                                                                            (
                                                                                fee
                                                                            ) => ({
                                                                                value:
                                                                                    fee.value,

                                                                                label:
                                                                                    fee.label,
                                                                            })
                                                                        )}
                                                                        onChange={(
                                                                            value
                                                                        ) =>
                                                                            changeCreateAllocation(
                                                                                index,
                                                                                "fee_type",
                                                                                value
                                                                            )
                                                                        }
                                                                    />
                                                                </Col>

                                                                <Col
                                                                    xs={
                                                                        24
                                                                    }
                                                                    md={
                                                                        8
                                                                    }
                                                                >
                                                                    <div className="field-label">
                                                                        Allocation
                                                                        Amount
                                                                    </div>

                                                                    <InputNumber
                                                                        style={{
                                                                            width:
                                                                                "100%",
                                                                        }}
                                                                        size="large"
                                                                        min={
                                                                            0
                                                                        }
                                                                        value={
                                                                            allocation.amount
                                                                        }
                                                                        placeholder="LRD Amount"
                                                                        prefix={
                                                                            <span
                                                                                style={{
                                                                                    color:
                                                                                        ORANGE,
                                                                                    fontWeight:
                                                                                        800,
                                                                                }}
                                                                            >
                                                                                LRD
                                                                            </span>
                                                                        }
                                                                        onChange={(
                                                                            value
                                                                        ) =>
                                                                            changeCreateAllocation(
                                                                                index,
                                                                                "amount",
                                                                                value ||
                                                                                    0
                                                                            )
                                                                        }
                                                                    />
                                                                </Col>

                                                                <Col
                                                                    xs={
                                                                        24
                                                                    }
                                                                    md={
                                                                        6
                                                                    }
                                                                >
                                                                    <div className="field-label">
                                                                        Action
                                                                    </div>

                                                                    <Button
                                                                        danger
                                                                        block
                                                                        size="large"
                                                                        icon={
                                                                            <DeleteOutlined />
                                                                        }
                                                                        onClick={() =>
                                                                            removeCreateAllocation(
                                                                                index
                                                                            )
                                                                        }
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </Col>
                                                            </Row>
                                                        </Card>
                                                    )
                                                )
                                            )}
                                        </Space>
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>
            </Card>

            {loading ? (
                <div className="loading-panel">
                    <Spin size="large" />

                    <Text
                        style={{
                            color:
                                "#555",
                            marginTop:
                                12,
                        }}
                    >
                        Loading financial records...
                    </Text>
                </div>
            ) : !selectedStudentDetails ? (
                <>
                    <div className="desktop-fee-table">
                        <Card
                            style={
                                mainCardStyle
                            }
                            title={
                                <div className="records-header">
                                    <div>
                                        <Text className="section-eyebrow">
                                            STUDENT DIRECTORY
                                        </Text>

                                        <div className="records-title">
                                            Student Fee Records
                                        </div>
                                    </div>

                                    <Space>
                                        <Tag
                                            color="orange"
                                        >
                                            {
                                                groupedRows.length
                                            }{" "}
                                            Students
                                        </Tag>

                                        <Tooltip title="Refresh Records">
                                            <Button
                                                icon={
                                                    <ReloadOutlined />
                                                }
                                                onClick={
                                                    fetchFees
                                                }
                                            />
                                        </Tooltip>
                                    </Space>
                                </div>
                            }
                        >
                            <Table
                                rowKey="student_id"
                                dataSource={
                                    groupedRows
                                }
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger:
                                        true,
                                    showTotal:
                                        (
                                            total
                                        ) =>
                                            `${total} students`,
                                }}
                                scroll={{
                                    x: 900,
                                }}
                                columns={[
                                    {
                                        title:
                                            "Student",

                                        dataIndex:
                                            "student_name",

                                        render: (
                                            name,
                                            row
                                        ) => (
                                            <Space>
                                                <Avatar
                                                    src={
                                                        AvatarStudent
                                                    }
                                                    icon={
                                                        <UserOutlined />
                                                    }
                                                    size={
                                                        46
                                                    }
                                                    style={{
                                                        border:
                                                            `2px solid ${ORANGE}`,
                                                    }}
                                                />

                                                <div>
                                                    <div
                                                        style={{
                                                            color:
                                                                WHITE,
                                                            fontWeight:
                                                                850,
                                                            fontSize:
                                                                14,
                                                        }}
                                                    >
                                                        {
                                                            name
                                                        }
                                                    </div>

                                                    <div
                                                        style={{
                                                            color:
                                                                MUTED,
                                                            fontSize:
                                                                12,
                                                            marginTop:
                                                                2,
                                                        }}
                                                    >
                                                        {
                                                            row.grade_class_name
                                                        }
                                                    </div>
                                                </div>
                                            </Space>
                                        ),
                                    },

                                    {
                                        title:
                                            "Student Type",

                                        dataIndex:
                                            "student_type",

                                        render:
                                            (
                                                value
                                            ) => (
                                                <Tag
                                                    color="orange"
                                                    style={{
                                                        fontWeight:
                                                            700,
                                                    }}
                                                >
                                                    {value ||
                                                        "Regular"}
                                                </Tag>
                                            ),
                                    },

                                    {
                                        title:
                                            "Records",

                                        dataIndex:
                                            "fee_records",

                                        render:
                                            (
                                                records
                                            ) => (
                                                <Badge
                                                    count={
                                                        records?.length ||
                                                        0
                                                    }
                                                    showZero
                                                    style={{
                                                        backgroundColor:
                                                            ORANGE,
                                                    }}
                                                />
                                            ),
                                    },

                                    {
                                        title:
                                            "Created By",

                                        dataIndex:
                                            "latest_created_by",

                                        render:
                                            (
                                                value
                                            ) => (
                                                <span
                                                    style={{
                                                        color:
                                                            "#ddd",
                                                    }}
                                                >
                                                    {value ||
                                                        "-"}
                                                </span>
                                            ),
                                    },

                                    {
                                        title:
                                            "Latest Activity",

                                        dataIndex:
                                            "latest_created_at",

                                        render:
                                            (
                                                value
                                            ) => (
                                                <span
                                                    style={{
                                                        color:
                                                            ORANGE,
                                                        whiteSpace:
                                                            "nowrap",
                                                        fontWeight:
                                                            750,
                                                    }}
                                                >
                                                    {formatDate(
                                                        value
                                                    )}
                                                </span>
                                            ),
                                    },

                                    {
                                        title:
                                            "Action",

                                        fixed:
                                            "right",

                                        render:
                                            (
                                                _,
                                                row
                                            ) => (
                                                <Button
                                                    type="primary"
                                                    icon={
                                                        <EyeOutlined />
                                                    }
                                                    style={{
                                                        background:
                                                            ORANGE,
                                                        borderColor:
                                                            ORANGE,
                                                        fontWeight:
                                                            800,
                                                    }}
                                                    onClick={() =>
                                                        openDetails(
                                                            row
                                                        )
                                                    }
                                                >
                                                    View Details
                                                </Button>
                                            ),
                                    },
                                ]}
                            />
                        </Card>
                    </div>

                    <div className="mobile-fee-cards">
                        <Space
                            direction="vertical"
                            style={{
                                width:
                                    "100%",
                            }}
                            size="middle"
                        >
                            {groupedRows.length ===
                            0 ? (
                                <Card
                                    style={
                                        mainCardStyle
                                    }
                                >
                                    <Empty
                                        description={
                                            <span
                                                style={{
                                                    color:
                                                        MUTED,
                                                }}
                                            >
                                                No fee records found
                                            </span>
                                        }
                                    />
                                </Card>
                            ) : (
                                groupedRows.map(
                                    (
                                        row
                                    ) => (
                                        <Card
                                            key={
                                                row.student_id
                                            }
                                            className="mobile-record-card"
                                            style={{
                                                ...mainCardStyle,
                                                width:
                                                    "100%",
                                            }}
                                        >
                                            <div className="mobile-card-top">
                                                <Space
                                                    align="start"
                                                >
                                                    <Avatar
                                                        size={
                                                            60
                                                        }
                                                        src={
                                                            AvatarStudent
                                                        }
                                                        style={{
                                                            border:
                                                                `2px solid ${ORANGE}`,
                                                        }}
                                                    />

                                                    <div>
                                                        <Title
                                                            level={
                                                                5
                                                            }
                                                            style={{
                                                                color:
                                                                    WHITE,
                                                                margin:
                                                                    0,
                                                                fontWeight:
                                                                    850,
                                                            }}
                                                        >
                                                            {
                                                                row.student_name
                                                            }
                                                        </Title>

                                                        <Text
                                                            style={{
                                                                color:
                                                                    MUTED,
                                                            }}
                                                        >
                                                            {
                                                                row.grade_class_name
                                                            }
                                                        </Text>
                                                    </div>
                                                </Space>

                                                <Badge
                                                    count={
                                                        row.fee_records?.length ||
                                                        0
                                                    }
                                                    showZero
                                                    style={{
                                                        backgroundColor:
                                                            ORANGE,
                                                    }}
                                                />
                                            </div>

                                            <Divider
                                                style={{
                                                    borderColor:
                                                        BORDER,
                                                }}
                                            />

                                            <div className="mobile-record-grid">
                                                <div>
                                                    <span>
                                                        STUDENT TYPE
                                                    </span>

                                                    <Tag color="orange">
                                                        {row.student_type ||
                                                            "Regular"}
                                                    </Tag>
                                                </div>

                                                <div>
                                                    <span>
                                                        CREATED BY
                                                    </span>

                                                    <strong>
                                                        {row.latest_created_by ||
                                                            "-"}
                                                    </strong>
                                                </div>

                                                <div>
                                                    <span>
                                                        LATEST ACTIVITY
                                                    </span>

                                                    <strong className="mobile-orange">
                                                        {formatDate(
                                                            row.latest_created_at
                                                        )}
                                                    </strong>
                                                </div>
                                            </div>

                                            <Button
                                                block
                                                size="large"
                                                type="primary"
                                                icon={
                                                    <EyeOutlined />
                                                }
                                                style={{
                                                    marginTop:
                                                        18,
                                                    background:
                                                        ORANGE,
                                                    borderColor:
                                                        ORANGE,
                                                    fontWeight:
                                                        850,
                                                }}
                                                onClick={() =>
                                                    openDetails(
                                                        row
                                                    )
                                                }
                                            >
                                                View Fee Details
                                            </Button>
                                        </Card>
                                    )
                                )
                            )}
                        </Space>
                    </div>
                </>
            ) : (
                <Card
                    style={
                        mainCardStyle
                    }
                >
                    <Button
                        icon={
                            <ArrowLeftOutlined />
                        }
                        onClick={() =>
                            setSelectedStudentDetails(
                                null
                            )
                        }
                        style={{
                            marginBottom:
                                20,
                            color:
                                ORANGE,
                            borderColor:
                                ORANGE,
                            background:
                                "transparent",
                            fontWeight:
                                800,
                        }}
                    >
                        Back to Fee Records
                    </Button>

                    <Row
                        gutter={[
                            20,
                            20,
                        ]}
                    >
                        <Col
                            xs={24}
                            lg={8}
                        >
                            <Card
                                className="details-profile-card"
                                style={{
                                    background:
                                        "linear-gradient(145deg, #242424, #0f0f0f)",
                                    border:
                                        "1px solid rgba(255,107,0,.32)",
                                    borderRadius:
                                        20,
                                    textAlign:
                                        "center",
                                }}
                            >
                                <div className="details-profile-decoration">
                                    <div className="details-avatar">
                                        <Avatar
                                            src={
                                                AvatarStudent
                                            }
                                            size={
                                                120
                                            }
                                        />
                                    </div>
                                </div>

                                <Title
                                    level={3}
                                    style={{
                                        color:
                                            WHITE,
                                        margin:
                                            "15px 0 3px",
                                        fontWeight:
                                            900,
                                    }}
                                >
                                    {
                                        selectedStudentDetails.student_name
                                    }
                                </Title>

                                <Text
                                    style={{
                                        color:
                                            MUTED,
                                    }}
                                >
                                    {
                                        selectedStudentDetails.grade_class_name
                                    }
                                </Text>

                                <div
                                    style={{
                                        marginTop:
                                            10,
                                    }}
                                >
                                    <Tag color="orange">
                                        {selectedStudentDetails.student_type ||
                                            "Regular"}
                                    </Tag>
                                </div>

                                <Divider
                                    style={{
                                        borderColor:
                                            BORDER,
                                    }}
                                />

                                <div className="balance-display">
                                    <Text
                                        style={{
                                            color:
                                                MUTED,
                                        }}
                                    >
                                        OVERALL BALANCE
                                    </Text>

                                    <div>
                                        <span>
                                            LRD
                                        </span>{" "}
                                        {formatMoney(
                                            currentFee?.overall_balance ||
                                                currentFee?.balance ||
                                                0
                                        )}
                                    </div>
                                </div>

                                <div className="record-navigation">
                                    <Button
                                        disabled={
                                            currentFeeIndex <=
                                            0
                                        }
                                        icon={
                                            <ArrowLeftOutlined />
                                        }
                                        onClick={() =>
                                            setCurrentFeeIndex(
                                                (
                                                    previous
                                                ) =>
                                                    Math.max(
                                                        previous -
                                                            1,
                                                        0
                                                    )
                                            )
                                        }
                                    >
                                        Previous
                                    </Button>

                                    <div className="record-counter">
                                        <span>
                                            RECORD
                                        </span>

                                        <strong>
                                            {currentFeeIndex +
                                                1}
                                            {" "}
                                            /{" "}
                                            {
                                                selectedStudentDetails
                                                    .fee_records
                                                    .length
                                            }
                                        </strong>
                                    </div>

                                    <Button
                                        disabled={
                                            currentFeeIndex >=
                                            selectedStudentDetails
                                                .fee_records
                                                .length -
                                                1
                                        }
                                        icon={
                                            <ArrowRightOutlined />
                                        }
                                        onClick={() =>
                                            setCurrentFeeIndex(
                                                (
                                                    previous
                                                ) =>
                                                    Math.min(
                                                        previous +
                                                            1,
                                                        selectedStudentDetails
                                                            .fee_records
                                                            .length -
                                                            1
                                                    )
                                            )
                                        }
                                    >
                                        Next
                                    </Button>
                                </div>

                                <Button
                                    block
                                    size="large"
                                    icon={
                                        <PrinterOutlined />
                                    }
                                    style={{
                                        marginTop:
                                            18,
                                        background:
                                            ORANGE,
                                        borderColor:
                                            ORANGE,
                                        color:
                                            WHITE,
                                        fontWeight:
                                            850,
                                    }}
                                    onClick={() =>
                                        setReceiptDialogOpen(
                                            true
                                        )
                                    }
                                >
                                    Print Receipt
                                </Button>
                            </Card>
                        </Col>

                        <Col
                            xs={24}
                            lg={16}
                        >
                            <Card
                                className="payment-details-card"
                                style={{
                                    background:
                                        DARK_CARD,
                                    border:
                                        "1px solid #333",
                                    borderRadius:
                                        20,
                                }}
                            >
                                <div className="details-header">
                                    <div>
                                        <Text className="section-eyebrow">
                                            PAYMENT RECORD
                                        </Text>

                                        <Title
                                            level={3}
                                            style={{
                                                color:
                                                    WHITE,
                                                margin:
                                                    "4px 0",
                                                fontWeight:
                                                    900,
                                            }}
                                        >
                                            Payment Details
                                        </Title>

                                        <Text
                                            style={{
                                                color:
                                                    MUTED,
                                            }}
                                        >
                                            {formatDate(
                                                currentFee?.created_at
                                            )}
                                        </Text>
                                    </div>

                                    <Space>
                                        <Tooltip title="Edit Fee">
                                            <Button
                                                size="large"
                                                icon={
                                                    <EditOutlined />
                                                }
                                                onClick={() =>
                                                    openEdit(
                                                        currentFee
                                                    )
                                                }
                                            />
                                        </Tooltip>

                                        <Tooltip title="Delete Fee">
                                            <Button
                                                size="large"
                                                danger
                                                icon={
                                                    <DeleteOutlined />
                                                }
                                                onClick={() =>
                                                    confirmDelete(
                                                        currentFee.id
                                                    )
                                                }
                                            />
                                        </Tooltip>
                                    </Space>
                                </div>

                                <Divider
                                    style={{
                                        borderColor:
                                            BORDER,
                                    }}
                                />

                                <div className="currency-status-banner">
                                    <div className="currency-status-icon">
                                        {currentCurrency ===
                                        "USD" ? (
                                            <CreditCardOutlined />
                                        ) : (
                                            <DollarOutlined />
                                        )}
                                    </div>

                                    <div>
                                        <span>
                                            PAYMENT CURRENCY
                                        </span>

                                        <strong>
                                            {
                                                currentCurrency
                                            }
                                        </strong>
                                    </div>

                                    {currentCurrency ===
                                        "USD" &&
                                        currentExchangeRate >
                                            0 && (
                                            <div className="currency-rate">
                                                <span>
                                                    EXCHANGE RATE
                                                </span>

                                                <strong>
                                                    1 USD ={" "}
                                                    {formatMoney(
                                                        currentExchangeRate
                                                    )}{" "}
                                                    LRD
                                                </strong>
                                            </div>
                                        )}
                                </div>

                                <Descriptions
                                    bordered
                                    column={{
                                        xs: 1,
                                        sm: 2,
                                        md: 2,
                                    }}
                                    labelStyle={{
                                        background:
                                            "#222",
                                        color:
                                            MUTED,
                                    }}
                                    contentStyle={{
                                        background:
                                            "#181818",
                                        color:
                                            WHITE,
                                        fontWeight:
                                            700,
                                    }}
                                >
                                    <Descriptions.Item
                                        label="Created By"
                                    >
                                        {currentFee?.created_by_name ||
                                            "-"}
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        label="Edited By"
                                    >
                                        {currentFee?.updated_by_name ||
                                            "-"}
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        label="Semester"
                                    >
                                        {currentFee?.semester ||
                                            "-"}
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        label="Payer"
                                    >
                                        {currentFee?.payer_name ||
                                            "-"}
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        label="Payment Currency"
                                    >
                                        <Tag
                                            color={
                                                currentCurrency ===
                                                "USD"
                                                    ? "blue"
                                                    : "green"
                                            }
                                        >
                                            {
                                                currentCurrency
                                            }
                                        </Tag>
                                    </Descriptions.Item>

                                    {currentCurrency ===
                                        "USD" &&
                                        currentExchangeRate >
                                            0 && (
                                            <Descriptions.Item
                                                label="Exchange Rate"
                                            >
                                                1 USD ={" "}
                                                {formatMoney(
                                                    currentExchangeRate
                                                )}{" "}
                                                LRD
                                            </Descriptions.Item>
                                        )}

                                    <Descriptions.Item
                                        label="Fee Charge"
                                    >
                                        <span className="money-white">
                                            LRD{" "}
                                            {formatMoney(
                                                currentFee?.fee_charge
                                            )}
                                        </span>
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        label="Payment Amount"
                                    >
                                        <div>
                                            <span className="payment-amount-main">
                                                {formatCurrency(
                                                    currentOriginalAmount,
                                                    currentCurrency
                                                )}
                                            </span>

                                            {currentCurrency ===
                                                "USD" && (
                                                <div className="normalized-note">
                                                    ≈ LRD{" "}
                                                    {formatMoney(
                                                        currentNormalizedAmount
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        label="Allocated"
                                    >
                                        <span className="money-orange">
                                            LRD{" "}
                                            {formatMoney(
                                                currentFee?.amount ||
                                                    0
                                            )}
                                        </span>
                                    </Descriptions.Item>

                                    <Descriptions.Item
                                        label="Balance"
                                    >
                                        <span className="money-orange">
                                            LRD{" "}
                                            {formatMoney(
                                                currentFee?.balance ||
                                                    0
                                            )}
                                        </span>
                                    </Descriptions.Item>
                                </Descriptions>

                                <Divider
                                    style={{
                                        borderColor:
                                            BORDER,
                                    }}
                                />

                                <div className="allocation-detail-header">
                                    <div>
                                        <Text className="section-eyebrow">
                                            BREAKDOWN
                                        </Text>

                                        <Title
                                            level={4}
                                            style={{
                                                color:
                                                    WHITE,
                                                margin:
                                                    "3px 0",
                                                fontWeight:
                                                    900,
                                            }}
                                        >
                                            Fee Allocations
                                        </Title>
                                    </div>

                                    <Tag color="blue">
                                        LRD
                                    </Tag>
                                </div>

                                <List
                                    dataSource={
                                        currentFee?.allocations ||
                                        []
                                    }
                                    locale={{
                                        emptyText:
                                            "No allocations found",
                                    }}
                                    renderItem={(
                                        allocation,
                                        index
                                    ) => (
                                        <List.Item
                                            className="detail-allocation-item"
                                        >
                                            <div className="detail-allocation-number">
                                                {String(
                                                    index +
                                                        1
                                                ).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </div>

                                            <div className="detail-allocation-name">
                                                <span>
                                                    FEE TYPE
                                                </span>

                                                <strong>
                                                    {getFeeTypeLabel(
                                                        allocation.fee_type
                                                    )}
                                                </strong>
                                            </div>

                                            <div className="detail-allocation-amount">
                                                <span>
                                                    AMOUNT
                                                </span>

                                                <strong>
                                                    LRD{" "}
                                                    {formatMoney(
                                                        allocation.amount
                                                    )}
                                                </strong>
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Card>
            )}

            <Modal
                open={
                    !!editingFee
                }
                onCancel={
                    closeEdit
                }
                title={
                    <div className="modal-heading">
                        <div className="modal-heading-icon">
                            <EditOutlined />
                        </div>

                        <div>
                            <strong>
                                Edit Fee Allocations
                            </strong>

                            <span>
                                Update the LRD allocation
                                breakdown
                            </span>
                        </div>
                    </div>
                }
                width={760}
                footer={[
                    <Button
                        key="cancel"
                        onClick={
                            closeEdit
                        }
                    >
                        Cancel
                    </Button>,

                    <Button
                        key="save"
                        type="primary"
                        loading={
                            editLoading
                        }
                        icon={
                            <CheckCircleOutlined />
                        }
                        onClick={
                            saveEdit
                        }
                        style={{
                            background:
                                ORANGE,
                            borderColor:
                                ORANGE,
                            fontWeight:
                                800,
                        }}
                    >
                        Save Changes
                    </Button>,
                ]}
            >
                {editingFee && (
                    <Space
                        direction="vertical"
                        style={{
                            width:
                                "100%",
                        }}
                        size="middle"
                    >
                        <Card
                            size="small"
                            className="edit-summary-card"
                        >
                            <div className="edit-summary-top">
                                <div>
                                    <span>
                                        PAYMENT CURRENCY
                                    </span>

                                    <Tag
                                        color={
                                            getPaymentCurrency(
                                                editingFee
                                            ) ===
                                            "USD"
                                                ? "blue"
                                                : "green"
                                        }
                                    >
                                        {
                                            getPaymentCurrency(
                                                editingFee
                                            )
                                        }
                                    </Tag>
                                </div>

                                <div>
                                    <span>
                                        AVAILABLE
                                    </span>

                                    <strong>
                                        LRD{" "}
                                        {formatMoney(
                                            getNormalizedAmount(
                                                editingFee
                                            )
                                        )}
                                    </strong>
                                </div>
                            </div>

                            {getPaymentCurrency(
                                editingFee
                            ) ===
                                "USD" &&
                                getPaymentExchangeRate(
                                    editingFee
                                ) >
                                    0 && (
                                    <div className="edit-exchange">
                                        1 USD ={" "}
                                        {formatMoney(
                                            getPaymentExchangeRate(
                                                editingFee
                                            )
                                        )}{" "}
                                        LRD
                                    </div>
                                )}
                        </Card>

                        {editAllocations.map(
                            (
                                allocation,
                                index
                            ) => (
                                <Card
                                    key={
                                        index
                                    }
                                    size="small"
                                    className="edit-allocation-card"
                                >
                                    <div className="allocation-number">
                                        {String(
                                            index +
                                                1
                                        ).padStart(
                                            2,
                                            "0"
                                        )}
                                    </div>

                                    <Row
                                        gutter={[
                                            12,
                                            12,
                                        ]}
                                    >
                                        <Col
                                            xs={
                                                24
                                            }
                                            sm={
                                                10
                                            }
                                        >
                                            <div className="field-label">
                                                Fee Type
                                            </div>

                                            <Select
                                                style={{
                                                    width:
                                                        "100%",
                                                }}
                                                size="large"
                                                value={
                                                    allocation.fee_type
                                                }
                                                placeholder="Fee Type"
                                                options={FEE_TYPE_CHOICES.map(
                                                    (
                                                        fee
                                                    ) => ({
                                                        value:
                                                            fee.value,
                                                        label:
                                                            fee.label,
                                                    })
                                                )}
                                                onChange={(
                                                    value
                                                ) =>
                                                    changeEditAllocation(
                                                        index,
                                                        "fee_type",
                                                        value
                                                    )
                                                }
                                            />
                                        </Col>

                                        <Col
                                            xs={
                                                24
                                            }
                                            sm={
                                                8
                                            }
                                        >
                                            <div className="field-label">
                                                Amount
                                            </div>

                                            <InputNumber
                                                style={{
                                                    width:
                                                        "100%",
                                                }}
                                                size="large"
                                                min={
                                                    0
                                                }
                                                value={
                                                    allocation.amount
                                                }
                                                prefix={
                                                    <span
                                                        style={{
                                                            color:
                                                                ORANGE,
                                                            fontWeight:
                                                                800,
                                                        }}
                                                    >
                                                        LRD
                                                    </span>
                                                }
                                                onChange={(
                                                    value
                                                ) =>
                                                    changeEditAllocation(
                                                        index,
                                                        "amount",
                                                        value ||
                                                            0
                                                    )
                                                }
                                            />
                                        </Col>

                                        <Col
                                            xs={
                                                24
                                            }
                                            sm={
                                                6
                                            }
                                        >
                                            <div className="field-label">
                                                Action
                                            </div>

                                            <Button
                                                danger
                                                block
                                                size="large"
                                                icon={
                                                    <DeleteOutlined />
                                                }
                                                onClick={() =>
                                                    removeEditAllocation(
                                                        index
                                                    )
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>
                            )
                        )}

                        <Button
                            block
                            size="large"
                            icon={
                                <PlusOutlined />
                            }
                            onClick={
                                addEditAllocation
                            }
                        >
                            Add Allocation
                        </Button>
                    </Space>
                )}
            </Modal>

            <Modal
                open={
                    !!deletingFeeId
                }
                onCancel={() =>
                    setDeletingFeeId(
                        null
                    )
                }
                title={
                    <div className="delete-modal-title">
                        <DeleteOutlined />
                        Confirm Delete
                    </div>
                }
                footer={[
                    <Button
                        key="cancel"
                        onClick={() =>
                            setDeletingFeeId(
                                null
                            )
                        }
                    >
                        Cancel
                    </Button>,

                    <Button
                        key="delete"
                        danger
                        type="primary"
                        icon={
                            <DeleteOutlined />
                        }
                        onClick={
                            doDelete
                        }
                    >
                        Delete Record
                    </Button>,
                ]}
            >
                <div className="delete-warning">
                    <div className="delete-warning-icon">
                        <DeleteOutlined />
                    </div>

                    <div>
                        <strong>
                            Are you sure?
                        </strong>

                        <p>
                            This fee record will be
                            permanently deleted. This
                            action cannot be undone.
                        </p>
                    </div>
                </div>
            </Modal>

            <ReceiptDialog
                open={
                    receiptDialogOpen
                }
                onClose={() =>
                    setReceiptDialogOpen(
                        false
                    )
                }
                studentDetails={
                    selectedStudentDetails
                }
                currentFeeIndex={
                    currentFeeIndex
                }
            />

            <style>
                {`
                    * {
                        box-sizing: border-box;
                    }

                    body {
                        margin: 0;
                        overflow-x: hidden;
                    }

                    .fee-dashboard-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 20px;
                        flex-wrap: wrap;
                        margin-bottom: 24px;
                    }

                    .dashboard-heading {
                        display: flex;
                        align-items: center;
                        gap: 14px;
                    }

                    .heading-icon {
                        width: 54px;
                        height: 54px;
                        border-radius: 16px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(145deg, #ff6b00, #c94300);
                        color: #fff;
                        font-size: 25px;
                        box-shadow: 0 10px 25px rgba(255,107,0,.28);
                    }

                    .academic-year-pill {
                        background: linear-gradient(145deg, #111, #202020);
                        border: 1px solid #ff6b00;
                        border-radius: 16px;
                        padding: 10px 20px;
                        min-width: 190px;
                        text-align: center;
                        display: flex;
                        flex-direction: column;
                        gap: 3px;
                        box-shadow: 0 10px 25px rgba(0,0,0,.2);
                    }

                    .year-label {
                        color: #8d8d8d;
                        font-size: 9px;
                        font-weight: 900;
                        letter-spacing: 1px;
                    }

                    .year-value {
                        color: #ff6b00;
                        font-size: 16px;
                        font-weight: 900;
                    }

                    .metric-card {
                        overflow: hidden;
                        position: relative;
                        min-height: 145px;
                        transition: transform .2s ease, box-shadow .2s ease;
                    }

                    .metric-card:hover {
                        transform: translateY(-3px);
                        box-shadow: 0 20px 40px rgba(0,0,0,.35) !important;
                    }

                    .metric-orange {
                        border-top: 3px solid #ff6b00 !important;
                    }

                    .metric-blue {
                        border-top: 3px solid #1677ff !important;
                    }

                    .metric-green {
                        border-top: 3px solid #52c41a !important;
                    }

                    .metric-red {
                        border-top: 3px solid #ff4d4f !important;
                    }

                    .metric-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 8px;
                    }

                    .metric-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,107,0,.12);
                        color: #ff6b00;
                        font-size: 19px;
                    }

                    .metric-icon.blue {
                        background: rgba(22,119,255,.12);
                        color: #1677ff;
                    }

                    .metric-icon.green {
                        background: rgba(82,196,26,.12);
                        color: #52c41a;
                    }

                    .metric-icon.red {
                        background: rgba(255,77,79,.12);
                        color: #ff4d4f;
                    }

                    .metric-title {
                        color: #999;
                        font-size: 12px;
                        font-weight: 700;
                    }

                    .overview-banner {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 20px;
                    }

                    .section-eyebrow {
                        color: #ff6b00 !important;
                        font-size: 10px;
                        font-weight: 900;
                        letter-spacing: 1.2px;
                    }

                    .overview-wallet {
                        width: 58px;
                        height: 58px;
                        border-radius: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,107,0,.12);
                        color: #ff6b00;
                        font-size: 27px;
                    }

                    .overview-stat {
                        display: flex;
                        align-items: center;
                        gap: 13px;
                        padding: 15px;
                        border-radius: 14px;
                        background: #181818;
                        border: 1px solid #303030;
                    }

                    .overview-stat-icon {
                        width: 42px;
                        height: 42px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,107,0,.12);
                        color: #ff6b00;
                    }

                    .overview-stat-icon.purple {
                        background: rgba(114,46,209,.15);
                        color: #b37feb;
                    }

                    .overview-label {
                        display: block;
                        color: #8d8d8d;
                        font-size: 10px;
                        font-weight: 800;
                        text-transform: uppercase;
                    }

                    .overview-value {
                        color: #fff;
                        font-size: 18px;
                        font-weight: 900;
                        margin-top: 3px;
                    }

                    .year-overview {
                        color: #ff6b00;
                    }

                    .status-panel {
                        display: flex;
                        align-items: center;
                        gap: 14px;
                    }

                    .status-circle {
                        width: 58px;
                        height: 58px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(82,196,26,.12);
                        color: #52c41a;
                        font-size: 28px;
                        box-shadow: 0 0 0 8px rgba(82,196,26,.04);
                    }

                    .create-section-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 20px;
                        padding: 22px 24px;
                        background: linear-gradient(90deg, #191919, #111);
                        border-bottom: 1px solid #303030;
                    }

                    .create-title-wrap {
                        display: flex;
                        align-items: center;
                        gap: 14px;
                    }

                    .create-title-icon {
                        width: 48px;
                        height: 48px;
                        border-radius: 14px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #ff6b00;
                        color: #fff;
                        font-size: 22px;
                        box-shadow: 0 8px 20px rgba(255,107,0,.22);
                    }

                    .create-section-body {
                        padding: 24px;
                    }

                    .field-label {
                        color: #a7a7a7;
                        font-size: 11px;
                        font-weight: 850;
                        text-transform: uppercase;
                        letter-spacing: .5px;
                        margin-bottom: 7px;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    }

                    .quick-info-box {
                        height: 100%;
                        min-height: 64px;
                        border-radius: 12px;
                        padding: 10px 14px;
                        background: #181818;
                        border: 1px solid #333;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                    }

                    .quick-info-box span {
                        color: #777;
                        font-size: 9px;
                        font-weight: 900;
                        letter-spacing: .7px;
                    }

                    .quick-info-box strong {
                        color: #fff;
                        font-size: 15px;
                        margin-top: 2px;
                    }

                    .currency-box {
                        align-items: flex-start;
                    }

                    .student-profile-create {
                        height: 100%;
                        overflow: hidden;
                    }

                    .profile-top {
                        text-align: center;
                    }

                    .avatar-ring {
                        display: inline-flex;
                        padding: 5px;
                        border-radius: 50%;
                        background: linear-gradient(145deg, #ff6b00, #6b2600);
                    }

                    .currency-highlight {
                        display: flex;
                        align-items: center;
                        gap: 11px;
                        padding: 12px;
                        background: #101010;
                        border: 1px solid #333;
                        border-radius: 12px;
                        margin-bottom: 10px;
                    }

                    .currency-icon {
                        width: 36px;
                        height: 36px;
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,107,0,.12);
                        color: #ff6b00;
                    }

                    .currency-highlight span,
                    .exchange-rate-box span {
                        display: block;
                        color: #777;
                        font-size: 8px;
                        font-weight: 900;
                        letter-spacing: .7px;
                    }

                    .currency-highlight strong,
                    .exchange-rate-box strong {
                        display: block;
                        color: #fff;
                        font-size: 14px;
                        margin-top: 2px;
                    }

                    .exchange-rate-box {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        padding: 10px 12px;
                        border-radius: 11px;
                        background: rgba(22,119,255,.08);
                        border: 1px solid rgba(22,119,255,.25);
                        margin-bottom: 12px;
                    }

                    .exchange-rate-box > div:first-child {
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 9px;
                        background: rgba(22,119,255,.15);
                        color: #1677ff;
                    }

                    .financial-mini-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 8px;
                    }

                    .financial-mini-grid > div {
                        padding: 10px;
                        border-radius: 11px;
                        background: #1d1d1d;
                        border: 1px solid #303030;
                    }

                    .financial-mini-grid span {
                        display: block;
                        color: #777;
                        font-size: 8px;
                        font-weight: 900;
                        text-transform: uppercase;
                    }

                    .financial-mini-grid strong {
                        display: block;
                        color: #fff;
                        font-size: 13px;
                        margin-top: 3px;
                    }

                    .orange-value {
                        color: #ff6b00 !important;
                    }

                    .fee-charge-box {
                        margin-top: 8px;
                        padding: 12px;
                        border-radius: 11px;
                        background: rgba(255,107,0,.07);
                        border: 1px solid rgba(255,107,0,.2);
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 10px;
                    }

                    .fee-charge-box span {
                        color: #888;
                        font-size: 9px;
                        font-weight: 900;
                    }

                    .fee-charge-box strong {
                        color: #ff6b00;
                        font-size: 14px;
                    }

                    .allocation-header,
                    .records-header,
                    .allocation-detail-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 14px;
                    }

                    .allocation-title,
                    .records-title {
                        color: #fff;
                        font-size: 19px;
                        font-weight: 900;
                        margin-top: 2px;
                    }

                    .allocation-progress {
                        padding: 15px;
                        background: #181818;
                        border: 1px solid #303030;
                        border-radius: 13px;
                    }

                    .allocation-progress-header,
                    .allocation-progress-footer {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 10px;
                    }

                    .allocation-progress-header span,
                    .allocation-progress-footer span {
                        color: #888;
                        font-size: 10px;
                        font-weight: 800;
                        text-transform: uppercase;
                    }

                    .allocation-progress-header strong {
                        color: #fff;
                        font-size: 12px;
                    }

                    .allocation-progress-footer {
                        margin-top: 7px;
                    }

                    .allocation-progress-footer strong {
                        color: #ff6b00;
                    }

                    .allocation-row-card,
                    .edit-allocation-card {
                        position: relative;
                        overflow: hidden;
                        padding-left: 42px !important;
                    }

                    .allocation-number {
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        width: 34px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #161616;
                        color: #ff6b00;
                        font-size: 10px;
                        font-weight: 900;
                        border-right: 1px solid #333;
                    }

                    .empty-allocation {
                        text-align: center;
                        padding: 45px 20px;
                        border: 1px dashed #444;
                        border-radius: 15px;
                        background: #181818;
                    }

                    .empty-icon {
                        width: 58px;
                        height: 58px;
                        margin: 0 auto;
                        border-radius: 17px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,107,0,.1);
                        color: #ff6b00;
                        font-size: 25px;
                    }

                    .loading-panel {
                        min-height: 240px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,255,255,.72);
                        border-radius: 20px;
                    }

                    .ant-card {
                        color: #fff;
                    }

                    .ant-card-head {
                        border-bottom: 1px solid #333 !important;
                    }

                    .ant-card-head-title {
                        color: #fff !important;
                    }

                    .ant-table {
                        background: #181818 !important;
                    }

                    .ant-table-container {
                        border-radius: 12px;
                        overflow: hidden;
                    }

                    .ant-table-thead > tr > th {
                        background: #ff6b00 !important;
                        color: #fff !important;
                        font-weight: 900 !important;
                        white-space: nowrap;
                    }

                    .ant-table-tbody > tr > td {
                        background: #181818 !important;
                        color: #fff !important;
                        border-bottom: 1px solid #333 !important;
                    }

                    .ant-table-tbody > tr:hover > td {
                        background: #292929 !important;
                    }

                    .ant-table-cell {
                        vertical-align: middle !important;
                    }

                    .ant-input-number,
                    .ant-select-selector,
                    .ant-input {
                        border-radius: 9px !important;
                    }

                    .ant-select-selector {
                        min-height: 42px !important;
                        display: flex !important;
                        align-items: center !important;
                    }

                    .ant-btn {
                        border-radius: 9px;
                        font-weight: 700;
                    }

                    .ant-statistic-content {
                        font-weight: 900;
                    }

                    .mobile-fee-cards {
                        display: none;
                    }

                    .desktop-fee-table {
                        display: block;
                    }

                    .mobile-record-card {
                        overflow: hidden;
                    }

                    .mobile-card-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .mobile-record-grid {
                        display: grid;
                        grid-template-columns: 1fr;
                        gap: 10px;
                    }

                    .mobile-record-grid > div {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 12px;
                        padding: 10px;
                        background: #181818;
                        border: 1px solid #303030;
                        border-radius: 10px;
                    }

                    .mobile-record-grid span {
                        color: #777;
                        font-size: 9px;
                        font-weight: 900;
                    }

                    .mobile-record-grid strong {
                        color: #fff;
                        font-size: 12px;
                        text-align: right;
                    }

                    .mobile-orange {
                        color: #ff6b00 !important;
                    }

                    .details-profile-decoration {
                        padding: 12px 0;
                    }

                    .details-avatar {
                        display: inline-flex;
                        padding: 5px;
                        border-radius: 50%;
                        background: linear-gradient(145deg, #ff6b00, #692600);
                    }

                    .balance-display {
                        padding: 18px;
                        border-radius: 15px;
                        background: linear-gradient(145deg, #251508, #17110d);
                        border: 1px solid rgba(255,107,0,.2);
                    }

                    .balance-display > div {
                        color: #ff6b00;
                        font-size: 27px;
                        font-weight: 900;
                        margin-top: 3px;
                    }

                    .balance-display > div span {
                        font-size: 12px;
                    }

                    .record-navigation {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;
                        flex-wrap: wrap;
                        margin-top: 18px;
                    }

                    .record-counter {
                        min-width: 85px;
                        text-align: center;
                        display: flex;
                        flex-direction: column;
                    }

                    .record-counter span {
                        color: #777;
                        font-size: 8px;
                        font-weight: 900;
                    }

                    .record-counter strong {
                        color: #fff;
                        font-size: 13px;
                    }

                    .currency-status-banner {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        flex-wrap: wrap;
                        padding: 13px;
                        border-radius: 13px;
                        background: #111;
                        border: 1px solid #333;
                        margin-bottom: 18px;
                    }

                    .currency-status-icon {
                        width: 42px;
                        height: 42px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,107,0,.12);
                        color: #ff6b00;
                        font-size: 19px;
                    }

                    .currency-status-banner span,
                    .currency-rate span {
                        display: block;
                        color: #777;
                        font-size: 8px;
                        font-weight: 900;
                        letter-spacing: .7px;
                    }

                    .currency-status-banner strong,
                    .currency-rate strong {
                        display: block;
                        color: #fff;
                        font-size: 13px;
                        margin-top: 2px;
                    }

                    .currency-rate {
                        margin-left: auto;
                        padding-left: 18px;
                        border-left: 1px solid #333;
                    }

                    .money-white {
                        color: #fff;
                        font-weight: 850;
                    }

                    .money-orange {
                        color: #ff6b00;
                        font-weight: 900;
                    }

                    .payment-amount-main {
                        color: #fff;
                        font-size: 15px;
                        font-weight: 900;
                    }

                    .normalized-note {
                        color: #888;
                        font-size: 10px;
                        margin-top: 4px;
                    }

                    .detail-allocation-item {
                        display: flex !important;
                        align-items: center !important;
                        gap: 12px;
                        background: #242424;
                        border: 1px solid #333;
                        border-radius: 11px;
                        margin-bottom: 8px;
                        padding: 12px !important;
                    }

                    .detail-allocation-number {
                        width: 32px;
                        height: 32px;
                        border-radius: 9px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(255,107,0,.1);
                        color: #ff6b00;
                        font-size: 10px;
                        font-weight: 900;
                    }

                    .detail-allocation-name,
                    .detail-allocation-amount {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;
                    }

                    .detail-allocation-name {
                        flex: 1;
                    }

                    .detail-allocation-name span,
                    .detail-allocation-amount span {
                        color: #777;
                        font-size: 8px;
                        font-weight: 900;
                    }

                    .detail-allocation-name strong {
                        color: #fff;
                        font-size: 13px;
                    }

                    .detail-allocation-amount {
                        text-align: right;
                    }

                    .detail-allocation-amount strong {
                        color: #ff6b00;
                        font-size: 13px;
                    }

                    .modal-heading {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }

                    .modal-heading > div:last-child {
                        display: flex;
                        flex-direction: column;
                    }

                    .modal-heading strong {
                        font-size: 16px;
                    }

                    .modal-heading span {
                        color: #888;
                        font-size: 11px;
                        font-weight: 500;
                    }

                    .modal-heading-icon {
                        width: 36px;
                        height: 36px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 10px;
                        background: rgba(255,107,0,.12);
                        color: #ff6b00;
                    }

                    .edit-summary-card {
                        background: #f8fafc !important;
                        border-radius: 13px !important;
                    }

                    .edit-summary-top {
                        display: flex;
                        justify-content: space-between;
                        gap: 20px;
                        flex-wrap: wrap;
                    }

                    .edit-summary-top > div {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .edit-summary-top span {
                        color: #64748b;
                        font-size: 10px;
                        font-weight: 900;
                    }

                    .edit-summary-top strong {
                        color: #111827;
                        font-size: 14px;
                    }

                    .edit-exchange {
                        margin-top: 12px;
                        padding-top: 10px;
                        border-top: 1px solid #e2e8f0;
                        color: #1677ff;
                        font-weight: 800;
                        font-size: 12px;
                    }

                    .delete-modal-title {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        color: #111;
                        font-weight: 900;
                    }

                    .delete-warning {
                        display: flex;
                        gap: 14px;
                        padding: 15px;
                        background: #fff1f0;
                        border: 1px solid #ffccc7;
                        border-radius: 12px;
                    }

                    .delete-warning-icon {
                        width: 42px;
                        height: 42px;
                        flex-shrink: 0;
                        border-radius: 11px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #ff4d4f;
                        color: #fff;
                    }

                    .delete-warning strong {
                        color: #cf1322;
                        font-size: 15px;
                    }

                    .delete-warning p {
                        color: #595959;
                        margin: 4px 0 0;
                        line-height: 1.5;
                    }

                    @media (max-width: 767px) {
                        .desktop-fee-table {
                            display: none;
                        }

                        .mobile-fee-cards {
                            display: block;
                        }

                        .fee-dashboard-header {
                            align-items: stretch;
                            flex-direction: column;
                        }

                        .dashboard-heading {
                            align-items: flex-start;
                        }

                        .heading-icon {
                            width: 46px;
                            height: 46px;
                            font-size: 20px;
                            flex-shrink: 0;
                        }

                        .academic-year-pill {
                            width: 100%;
                        }

                        .create-section-header {
                            align-items: flex-start;
                            flex-direction: column;
                        }

                        .create-section-body {
                            padding: 16px;
                        }

                        .currency-rate {
                            margin-left: 0;
                            padding-left: 0;
                            padding-top: 10px;
                            border-left: 0;
                            border-top: 1px solid #333;
                            width: 100%;
                        }

                        .details-header {
                            align-items: stretch;
                        }

                        .details-header > .ant-space {
                            width: 100%;
                        }

                        .details-header > .ant-space .ant-btn {
                            flex: 1;
                        }

                        .overview-banner {
                            align-items: flex-start;
                        }

                        .overview-wallet {
                            width: 46px;
                            height: 46px;
                            font-size: 21px;
                            flex-shrink: 0;
                        }

                        .allocation-detail-header {
                            align-items: flex-start;
                        }
                    }

                    @media (max-width: 576px) {
                        .ant-card {
                            border-radius: 14px !important;
                        }

                        .ant-statistic-content {
                            font-size: 22px !important;
                        }

                        .ant-descriptions-item-label,
                        .ant-descriptions-item-content {
                            font-size: 13px !important;
                        }

                        .ant-modal {
                            max-width: calc(100vw - 24px) !important;
                            margin: 12px auto !important;
                        }

                        .ant-modal-content {
                            border-radius: 14px !important;
                        }

                        .financial-mini-grid {
                            grid-template-columns: 1fr;
                        }

                        .create-title-wrap {
                            align-items: flex-start;
                        }

                        .create-title-icon {
                            width: 42px;
                            height: 42px;
                            flex-shrink: 0;
                        }

                        .allocation-title,
                        .records-title {
                            font-size: 17px;
                        }

                        .detail-allocation-item {
                            align-items: flex-start !important;
                        }

                        .detail-allocation-amount {
                            margin-left: auto;
                        }

                        .record-navigation {
                            gap: 5px;
                        }

                        .record-navigation .ant-btn {
                            padding-left: 8px;
                            padding-right: 8px;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default OtherFee;